import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import CalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import GiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { getFormattedDateByTimezone } from 'lib/experiences/experienceUtils'

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`

const Card = styled(Pane)`
  padding: ${rem(24)};
  border-radius: 2px;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
  flex-grow: 1;
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  margin-block-start: ${rem(20)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
  }
`

interface Props {
  experience: App.ExperienceOffer
  onBuyNowBnbl: () => void
  onGiveAsGift: () => void
}

function ExperienceOfferPageBnblAvailable(props: Props) {
  const { experience, onBuyNowBnbl, onGiveAsGift } = props
  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()

  const handleOnBuyNowBnbl = useCallback(() => {
    onBuyNowBnbl()
    trackEvent('BNBL_BUTTON_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleOnGiveAsGift = useCallback(() => {
    onGiveAsGift()
    trackEvent('GIVE_AS_GIFT_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const isVoucherBased = !experience.hasCalendar

  const bookUntilDate = (experience.expirationDate ?? experience.bookByDate)
  const formattedBookUntilDate = getFormattedDateByTimezone(bookUntilDate, experience.location.timezone, DMY_CASUAL_FORMAT)

  const showBNBLOption = !isVoucherBased && experience.allowBuyNowBookLater
  const showGiftCardOption = showBNBLOption || (!!experience.giftType && experience.allowGiftCard)

  const giftDescription = formattedBookUntilDate ? intl.formatMessage(
    {
      id: 'M7EH-UOKvMSMBPIpEPSn',
      defaultMessage: 'Surprise a special someone with the gift of an unforgettable experience. They can book until {date}.',
      description: 'Gift card description with booking deadline date on experience PDP',
    },
    { date: formattedBookUntilDate },
  ) : intl.formatMessage({
    id: '2RC1_g0oedTLXyMyKJb2',
    defaultMessage: 'Surprise a special someone with the gift of an unforgettable experience.',
    description: 'Gift card description on experience PDP on the gift card purchase or redemption page',
  })

  if (!showBNBLOption && !showGiftCardOption) {
    return null
  }

  return <SectionContainer>
    {!isVoucherBased && <Heading variant="heading4">
      {intl.formatMessage({
        id: 'azyPEhccMt6g9KDRrUDB',
        defaultMessage: 'Not ready to book just yet?',
        description: 'Heading for buy now book later section on experience PDP',
      })}
    </Heading>}
    <CardsContainer>
      {showBNBLOption && <Card>
        <VerticalSpacer gap={4}>
          <CalendarIcon />
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'rseog5XgzyCKzFtaN0eb',
              defaultMessage: 'Buy now, choose dates later',
              description: 'Heading for buy now book later card on experience PDP',
            })}
          </Heading>
          {!!formattedBookUntilDate && <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: 'REh-PUqT27pb1CT5akmp',
                defaultMessage: 'You can purchase this experience and choose dates when ready. Book until {date}.',
                description: 'Description for buy now book later option with booking deadline date',
              },
              { date: formattedBookUntilDate },
            )}
          </BodyText>}
          <TextButton
            kind="secondary"
            size="large"
            onClick={handleOnBuyNowBnbl}
            data-testid="experience-bnbl-buy-now-button"
          >
            {intl.formatMessage({
              id: 'RGaD_sHI8WME86nTDkMG',
              defaultMessage: 'Buy now',
              description: 'Button text for buy now book later on experience PDP',
            })}
          </TextButton>
        </VerticalSpacer>
      </Card>}
      {showGiftCardOption && <Card>
        <VerticalSpacer gap={4}>
          <GiftIcon />
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'MtP5YRuRj7tdThnspxBH',
              defaultMessage: 'Give this experience as a gift',
              description: 'Section heading for give as gift card on experience PDP',
            })}
          </Heading>
          <BodyText variant="medium">
            {giftDescription}
          </BodyText>
          <TextButton
            kind="secondary"
            size="large"
            onClick={handleOnGiveAsGift}
          >
            {intl.formatMessage({
              id: 'Mp6d1a9U6_42Mp1lWL4t',
              defaultMessage: 'Give as gift',
              description: 'Action button label to give experience as a gift on PDP',
            })}
          </TextButton>
        </VerticalSpacer>
      </Card>}
    </CardsContainer>
  </SectionContainer>
}

export default ExperienceOfferPageBnblAvailable
